import { type Schemas } from "@/types";
import { Popover, ActionIcon, Combobox, ScrollArea, Text } from "@mantine/core";
import { IconClock } from "@tabler/icons-react";
import { type Dispatch, type SetStateAction, useRef, useState } from "react";
import {
  calculateNewEndDate,
  calculateNewStartDate,
  isMultiDayEvent,
  recalculateEndTime,
} from "./timeUtils";

interface CustomStartTimeControlProps {
  startDate: Date | null;
  endDate: Date | null;
  setEndDate: (endDate: Date) => void;
  setStartDate: (startDate: Date) => void;
  duration: string;
  setPhantomEvent?: Dispatch<
    SetStateAction<Schemas["AppointmentRetrieveDto"] | null>
  >;
  refStart?: React.RefObject<HTMLInputElement>;
  refEnd?: React.RefObject<HTMLInputElement>;
  setEndTime: (endTime: string) => void;
  startTime: string;
  setStartTime: (endTime: string) => void;
}
export function CustomStartTimeControl({
  startDate,
  endDate,
  setEndDate,
  setStartDate,
  duration,
  setPhantomEvent,
  refStart,
  refEnd,
  setEndTime,
  startTime,
  setStartTime,
}: CustomStartTimeControlProps) {
  const viewportRef = useRef<HTMLDivElement>(null);

  const [StartTimeSelectorOpen, setStartTimeSelectorOpen] = useState(false);

  const isStartTimeSelected = (optionValue: string): boolean => {
    const formattedDate = startDate?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return optionValue === startTime || optionValue === formattedDate;
  };

  const generateTimeOptions = (
    startHour = 0,
    startMinute = 0,
    //startDate = startDate ?? null,
    //endDate = endDate ?? null,
  ) => {
    const times = [];
    const multiDay = isMultiDayEvent(
      startDate ?? new Date(),
      endDate ?? new Date(),
    );
    const startTimeInMinutes = startHour * 60 + startMinute;

    for (let i = 0; i < 96; i++) {
      const currentTimeInMinutes = i * 15 + startTimeInMinutes;
      const currentHour = Math.floor(currentTimeInMinutes / 60) % 24; // Wrap around past midnight
      const currentMinute = currentTimeInMinutes % 60;

      const timeString = `${currentHour.toString().padStart(2, "0")}:${currentMinute.toString().padStart(2, "0")}`;

      // Calculate duration relative to start time
      const durationMinutes = (i + 1) * 15; // Duration increases by 15 minutes each step
      const durationHours = Math.floor(durationMinutes / 60);
      const durationRemainingMinutes = durationMinutes % 60;
      const durationString = `${durationHours}h ${durationRemainingMinutes}m`;

      times.push({
        label: timeString,
        value: timeString,
        duration: durationString,
      });
    }

    if (startDate) {
      const endHour = startDate.getHours();
      const endMinute = startDate.getMinutes();
      const endTimeString = `${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;

      if (!times.some((t) => t.value === endTimeString)) {
        const totalDurationMinutes =
          (endHour - startHour) * 60 + (endMinute - startMinute);
        const endDurationHours = Math.floor(totalDurationMinutes / 60);
        const endDurationMinutes = totalDurationMinutes % 60;
        const endDurationString = `${endDurationHours}h ${endDurationMinutes}m`;

        times.push({
          label: endTimeString,
          value: endTimeString,
          duration: endDurationString,
        });
      }
    }

    if (endDate) {
      const endHour = endDate.getHours();
      const endMinute = endDate.getMinutes();
      const endTimeString = `${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;

      if (!times.some((t) => t.value === endTimeString)) {
        const totalDurationMinutes =
          (endHour - startHour) * 60 + (endMinute - startMinute);
        const endDurationHours = Math.floor(totalDurationMinutes / 60);
        const endDurationMinutes = totalDurationMinutes % 60;
        const endDurationString = `${endDurationHours}h ${endDurationMinutes}m`;

        times.push({
          label: endTimeString,
          value: endTimeString,
          duration: endDurationString,
        });
      }
    }

    if (!multiDay) {
      times.sort((a, b) => {
        const [aHours, aMinutes] = a.duration
          .split("h ")
          .map((part) => parseInt(part.replace("m", ""), 10) || 0);

        const [bHours, bMinutes] = b.duration
          .split("h ")
          .map((part) => parseInt(part.replace("m", ""), 10) || 0);

        return (
          (aHours ?? 0) * 60 +
          (aMinutes ?? 0) -
          ((bHours ?? 0) * 60 + (bMinutes ?? 0))
        );
      });
    } else {
      times.sort((a, b) => {
        const parseTime = (time: string) => {
          const [hours, minutes] = time.split(":").map(Number);
          return (hours ?? 0) * 60 + (minutes ?? 0);
        };

        return parseTime(a.value) - parseTime(b.value);
      });
    }
    return times;
  };

  const startTimeOptions = generateTimeOptions();

  return (
    <>
      <Popover
        width={200}
        position="bottom"
        withArrow
        shadow="md"
        opened={StartTimeSelectorOpen}
        onChange={setStartTimeSelectorOpen}
      >
        <Popover.Target>
          <ActionIcon
            variant="subtle"
            color="primary"
            onClick={() => setStartTimeSelectorOpen((o) => !o)}
          >
            <IconClock style={{ width: 16, height: 16 }} stroke={1.5} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Combobox
            onOptionSubmit={(val) => {
              // recalculate start date
              const newStartDate = calculateNewStartDate(
                startDate ?? new Date(),
                val,
              );

              if (refStart) {
                if (refStart.current) {
                  refStart.current.value = val;
                }
              }

              // recalculate end time
              const newEndTime = recalculateEndTime(val, duration);

              if (refEnd) {
                if (refEnd.current) {
                  refEnd.current.value = newEndTime;
                }
              }

              // recalculate end date
              const newEndDate = calculateNewEndDate(
                startDate ?? new Date(),
                endDate ?? new Date(),
                newEndTime,
                newStartDate,
              );

              // set new values
              setStartDate(newStartDate);
              setStartTime(val);
              setStartTimeSelectorOpen(false);
              setEndTime(newEndTime);
              setEndDate(newEndDate);

              if (setPhantomEvent) {
                setPhantomEvent({
                  startDate: newStartDate?.toString(),
                  endDate: newEndDate?.toString(),
                });
              }
            }}
          >
            <Combobox.Options mah={200} style={{ overflowY: "auto" }}>
              <ScrollArea.Autosize
                type="scroll"
                mah={200}
                viewportRef={viewportRef}
              >
                {startTimeOptions.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    value={option.value}
                    active={isStartTimeSelected(option.value)}
                    selected={isStartTimeSelected(option.value)}
                    styles={{
                      option: {
                        backgroundColor: isStartTimeSelected(option.value)
                          ? "#E9F6FF"
                          : "",
                        color: isStartTimeSelected(option.value)
                          ? "#468ecb"
                          : "",
                      },
                    }}
                    ref={(el) => {
                      if (el && isStartTimeSelected(option.value)) {
                        el.scrollIntoView({
                          block: "center",
                        });
                      }
                    }} // Scrolls immediately when rendered
                  >
                    {
                      <Text fw={600} size="xs">
                        {option.label}
                      </Text>
                    }
                  </Combobox.Option>
                ))}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox>
        </Popover.Dropdown>
      </Popover>
    </>
  );
}
